import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EmailServerTestResult, EmailServerView } from '@cloudsign/shared';
import type { EmailSecurity } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Button, Card, CheckboxField, SelectField, Spinner, TextField } from '../../components/ui';

const URL = '/platform/email-server';

interface Form {
  transport: 'file' | 'smtp';
  host: string;
  port: string;
  security: EmailSecurity;
  username: string;
  password: string;
  changePassword: boolean;
  clearPassword: boolean;
  allowInsecureAuth: boolean;
  fromName: string;
  fromAddress: string;
  replyTo: string;
}

const fromView = (v: EmailServerView): Form => ({
  transport: v.transport,
  host: v.host,
  port: String(v.port),
  security: v.security,
  username: v.username,
  password: '',
  changePassword: false,
  clearPassword: false,
  allowInsecureAuth: v.allowInsecureAuth,
  fromName: v.fromName,
  fromAddress: v.fromAddress,
  replyTo: v.replyTo,
});

/** Platform > Settings > Email server: the outgoing mail server, saved in the database (the environment variables are only the fallback). */
export function EmailServerSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['platform', 'email-server'], queryFn: () => api.get(URL, { schema: EmailServerView }) });
  const [form, setForm] = useState<Form | null>(null);
  useEffect(() => {
    if (q.data) setForm(fromView(q.data));
  }, [q.data]);

  const save = useMutation({
    mutationFn: (f: Form) =>
      api.put(URL, {
        body: {
          transport: f.transport,
          host: f.host,
          port: Number(f.port),
          security: f.security,
          username: f.username,
          password: f.changePassword ? f.password : '',
          clearPassword: f.clearPassword,
          allowInsecureAuth: f.allowInsecureAuth,
          fromName: f.fromName,
          fromAddress: f.fromAddress,
          replyTo: f.replyTo,
        },
        schema: EmailServerView,
      }),
    onSuccess: (v) => {
      qc.setQueryData(['platform', 'email-server'], v);
      toast(t('emailServer.saved'));
    },
  });

  const [to, setTo] = useState('');
  const test = useMutation({
    mutationFn: () => api.post(`${URL}/test`, { body: { to }, schema: EmailServerTestResult }),
  });

  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data || !form) return <Spinner />;
  const view = q.data;
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));
  const errs = fieldErrors(save.error);
  const smtp = form.transport === 'smtp';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(form);
  };

  return (
    <div className="mt-8 space-y-6">
      <Card title={t('emailServer.title')} description={t('emailServer.description')}>
        <Alert kind="info">{t(view.source === 'database' ? 'emailServer.source.database' : 'emailServer.source.environment')}</Alert>
        {view.warnings.map((w) => (
          <div key={w} className="mt-3">
            <Alert kind="error">{t(w)}</Alert>
          </div>
        ))}
        <form onSubmit={submit} className="mt-4 grid gap-4 sm:grid-cols-2" aria-label={t('emailServer.title')}>
          <div className="sm:col-span-2">
            <ErrorLine error={save.error && Object.keys(errs).length === 0 ? save.error : null} />
          </div>
          <SelectField
            label={t('emailServer.transport')}
            value={form.transport}
            disabled={!canEdit}
            onChange={(e) => set('transport', e.target.value as Form['transport'])}
            className="sm:col-span-2"
          >
            <option value="smtp">{t('emailServer.transport.smtp')}</option>
            <option value="file">{t('emailServer.transport.file')}</option>
          </SelectField>
          {smtp ? (
            <>
              <TextField
                label={t('emailServer.host')}
                hint={t('emailServer.host.hint')}
                value={form.host}
                disabled={!canEdit}
                onChange={(e) => set('host', e.target.value)}
                error={errs['host']}
                autoComplete="off"
              />
              <TextField
                label={t('emailServer.port')}
                type="number"
                min={1}
                max={65535}
                value={form.port}
                disabled={!canEdit}
                onChange={(e) => set('port', e.target.value)}
                error={errs['port']}
              />
              <SelectField
                label={t('emailServer.security')}
                value={form.security}
                disabled={!canEdit}
                onChange={(e) => set('security', e.target.value as EmailSecurity)}
                error={errs['security']}
              >
                <option value="starttls">{t('emailServer.security.starttls')}</option>
                <option value="tls">{t('emailServer.security.tls')}</option>
                <option value="none">{t('emailServer.security.none')}</option>
              </SelectField>
              <TextField
                label={t('emailServer.username')}
                hint={t('emailServer.username.hint')}
                value={form.username}
                disabled={!canEdit}
                onChange={(e) => set('username', e.target.value)}
                error={errs['username']}
                autoComplete="off"
              />
              <div>
                {form.changePassword ? (
                  <>
                    <TextField
                      label={t('emailServer.password.new')}
                      type="password"
                      value={form.password}
                      disabled={!canEdit}
                      onChange={(e) => set('password', e.target.value)}
                      autoComplete="new-password"
                    />
                    <Button className="mt-2" onClick={() => setForm({ ...form, changePassword: false, password: '' })}>
                      {t('emailServer.password.cancel')}
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="mb-1 text-sm font-medium text-slate-800">{t('emailServer.password')}</p>
                    <p className="text-sm text-slate-600">{t(view.passwordSet && !form.clearPassword ? 'emailServer.password.set' : 'emailServer.password.notSet')}</p>
                    {canEdit ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button onClick={() => setForm({ ...form, changePassword: true, clearPassword: false })}>{t('emailServer.password.change')}</Button>
                        {view.passwordSet && !form.clearPassword ? (
                          <Button onClick={() => set('clearPassword', true)}>{t('emailServer.password.clear')}</Button>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
              {form.security === 'none' ? (
                <CheckboxField
                  className="sm:col-span-2"
                  label={t('emailServer.allowInsecureAuth')}
                  hint={t('emailServer.allowInsecureAuth.hint')}
                  checked={form.allowInsecureAuth}
                  disabled={!canEdit}
                  onChange={(e) => set('allowInsecureAuth', e.target.checked)}
                />
              ) : null}
            </>
          ) : null}
          <TextField
            label={t('emailServer.fromName')}
            value={form.fromName}
            disabled={!canEdit}
            onChange={(e) => set('fromName', e.target.value)}
            error={errs['fromName']}
          />
          <TextField
            label={t('emailServer.fromAddress')}
            type="email"
            value={form.fromAddress}
            disabled={!canEdit}
            onChange={(e) => set('fromAddress', e.target.value)}
            error={errs['fromAddress']}
          />
          <TextField
            label={t('emailServer.replyTo')}
            hint={t('emailServer.replyTo.hint')}
            type="email"
            value={form.replyTo}
            disabled={!canEdit}
            onChange={(e) => set('replyTo', e.target.value)}
            error={errs['replyTo']}
            className="sm:col-span-2"
          />
          {canEdit ? (
            <div className="flex justify-end sm:col-span-2">
              <Button type="submit" variant="primary" disabled={save.isPending}>
                {t('emailServer.save')}
              </Button>
            </div>
          ) : null}
        </form>
      </Card>

      {canEdit ? (
        <Card title={t('emailServer.test.title')} description={t('emailServer.test.description')}>
          <form
            className="grid gap-4 sm:grid-cols-3"
            aria-label={t('emailServer.test.title')}
            onSubmit={(e) => {
              e.preventDefault();
              test.mutate();
            }}
          >
            <TextField label={t('emailServer.test.to')} type="email" required value={to} onChange={(e) => setTo(e.target.value)} className="sm:col-span-2" />
            <div className="flex items-end">
              <Button type="submit" disabled={test.isPending || to === ''}>
                {t('emailServer.test.send')}
              </Button>
            </div>
            <div className="sm:col-span-3" aria-live="polite">
              <ErrorLine error={test.error} />
              {test.data ? (
                test.data.ok ? (
                  <Alert kind="success">{t('emailServer.test.ok')}</Alert>
                ) : (
                  <Alert kind="error">{t('emailServer.test.failed', { error: test.data.error ?? '' })}</Alert>
                )
              ) : null}
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}
